import type { AppConfig } from "../../types/appConfig";
import type {
  Excursion,
  ExcursionRequest,
  ExcursionParams,
  ExcursionParamsInput,
  ExcursionGroupParams,
  ExcursionGroupParamsInput,
  ExcursionBooking,
  ExcursionBookingFilters,
  CreateExcursionBookingRequest,
  UpdateExcursionBookingRequest,
} from "./Excursion.typed";
import type { MultiComment } from "../zekay/comments";
import type {
  ExcursionSMSMessagesResponse,
  SendExcursionSMSRequest,
  SendExcursionSMSResponse,
} from "../smsTemplates/SMSTemplate.typed";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import { asOptionalString } from "../zekay/jsonNormalize";

function normalizeExcursionBooking(raw: ExcursionBooking): ExcursionBooking {
  return {
    ...raw,
    user_name: asOptionalString(raw.user_name),
    company_name: asOptionalString(raw.company_name),
    planning_slot_label: asOptionalString(raw.planning_slot_label),
    planning_anonymized: raw.planning_anonymized === true,
    excursion_template: asOptionalString(raw.excursion_template),
    group_template: asOptionalString(raw.group_template),
    date: asOptionalString(raw.date),
  };
}

function normalizeExcursionBookingList(payload: unknown): ExcursionBooking[] {
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeExcursionBooking(item as ExcursionBooking));
  }

  if (payload && typeof payload === "object" && "data" in (payload as Record<string, unknown>)) {
    const data = (payload as { data?: unknown }).data;
    if (!Array.isArray(data)) return [];
    return data.map((item) => normalizeExcursionBooking(item as ExcursionBooking));
  }

  return [];
}

function normalizeGroupParamRow(t: ExcursionGroupParams & { ID?: number }): ExcursionGroupParams {
  const id = t.id ?? t.ID;
  return { ...t, id: id != null && !Number.isNaN(Number(id)) ? Number(id) : t.id };
}

function normalizeExcursionParamRow(t: ExcursionParams & { ID?: number }): ExcursionParams {
  const id = t.id ?? t.ID;
  return { ...t, id: id != null && !Number.isNaN(Number(id)) ? Number(id) : t.id };
}

export class ExcursionModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async listExcursions(from: string, to: string): Promise<Excursion[]> {
    const params = new URLSearchParams({ from, to });
    const response = await fetchZekay(`${this.zekayAPI}/excursions?${params}`, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return (await response.json()) as Excursion[];
  }

  async getExcursion(id: number): Promise<Excursion> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/${id}`, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    await assertResponseOk(response);
    return (await response.json()) as Excursion;
  }

  private static readonly BATCH_MAX_IDS = 100;

  async getExcursionsBatch(ids: number[]): Promise<Excursion[]> {
    if (ids.length === 0) return [];
    const unique = Array.from(new Set(ids));
    const chunks: number[][] = [];
    for (let i = 0; i < unique.length; i += ExcursionModel.BATCH_MAX_IDS) {
      chunks.push(unique.slice(i, i + ExcursionModel.BATCH_MAX_IDS));
    }
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const response = await fetchZekay(`${this.zekayAPI}/excursions/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: chunk }),
        });
        await expectStatuses(response, 200, 204);
        if (response.status === 204) return [];
        return (await response.json()) as Excursion[];
      }),
    );
    return results.flat();
  }

  async createExcursion(payload: ExcursionRequest): Promise<Excursion> {
    const body = JSON.stringify(payload);
    const response = await fetchZekay(`${this.zekayAPI}/excursions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    await expectStatuses(response, 201);
    const json = (await response.json()) as Excursion;
    return json;
  }

  async updateExcursion(id: number, payload: ExcursionRequest): Promise<Excursion> {
    const body = JSON.stringify(payload);
    const response = await fetchZekay(`${this.zekayAPI}/excursions/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body,
    });
    await assertResponseOk(response);
    const json = (await response.json()) as Excursion;
    return json;
  }

  async deleteExcursion(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async listExcursionParams(): Promise<ExcursionParams[]> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/params`);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    const raw = (await response.json()) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => normalizeExcursionParamRow(row as ExcursionParams & { ID?: number }));
  }

  async getExcursionParams(): Promise<ExcursionParams | null> {
    const list = await this.listExcursionParams();
    return list.find((t) => t.should_be_used_by_default) ?? list[0] ?? null;
  }

  async getExcursionParamsById(id: number): Promise<ExcursionParams | null> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/params/${id}`);
    if (response.status === 404) return null;
    await assertResponseOk(response);
    return (await response.json()) as ExcursionParams;
  }

  async createExcursionParams(payload: ExcursionParamsInput): Promise<ExcursionParams> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/params`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 201);
    return (await response.json()) as ExcursionParams;
  }

  async updateExcursionParams(id: number, payload: ExcursionParamsInput): Promise<ExcursionParams> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/params/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(response);
    return (await response.json()) as ExcursionParams;
  }

  async deleteExcursionParams(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/params/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async listExcursionGroupParams(): Promise<ExcursionGroupParams[]> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/group-params`);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    const raw = (await response.json()) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => normalizeGroupParamRow(row as ExcursionGroupParams & { ID?: number }));
  }

  async getExcursionGroupParams(): Promise<ExcursionGroupParams | null> {
    const list = await this.listExcursionGroupParams();
    return list.find((t) => t.should_be_used_by_default) ?? list[0] ?? null;
  }

  async getExcursionGroupParamsById(id: number): Promise<ExcursionGroupParams | null> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/group-params/${id}`);
    if (response.status === 404) return null;
    await assertResponseOk(response);
    return (await response.json()) as ExcursionGroupParams;
  }

  async createExcursionGroupParams(
    payload: ExcursionGroupParamsInput,
  ): Promise<ExcursionGroupParams> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/group-params`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 201);
    return (await response.json()) as ExcursionGroupParams;
  }

  async updateExcursionGroupParams(
    id: number,
    payload: ExcursionGroupParamsInput,
  ): Promise<ExcursionGroupParams> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/group-params/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(response);
    return (await response.json()) as ExcursionGroupParams;
  }

  async deleteExcursionGroupParams(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/group-params/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async updateExcursionGroupComments(
    excursionId: number,
    groupId: number,
    comments: MultiComment[],
  ): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/excursions/${excursionId}/groups/${groupId}/comments`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comments }),
      },
    );
    await expectStatuses(response, 204);
  }

  async listExcursionBookings(filters?: ExcursionBookingFilters): Promise<ExcursionBooking[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.excursion_id) params.set("excursion_id", String(filters.excursion_id));
    if (filters?.startTime) params.set("startTime", filters.startTime);
    if (filters?.endTime) params.set("endTime", filters.endTime);
    if (filters?.from) params.set("from", filters.from);
    if (filters?.to) params.set("to", filters.to);
    if (filters?.scope) params.set("scope", filters.scope);
    const qs = params.toString();
    const response = await fetchZekay(`${this.zekayAPI}/excursion-bookings${qs ? `?${qs}` : ""}`);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return normalizeExcursionBookingList(await response.json());
  }

  async getExcursionBooking(id: number): Promise<ExcursionBooking> {
    const response = await fetchZekay(`${this.zekayAPI}/excursion-bookings/${id}`);
    await assertResponseOk(response);
    return normalizeExcursionBooking(await response.json());
  }

  async createExcursionBooking(payload: CreateExcursionBookingRequest): Promise<ExcursionBooking> {
    const response = await fetchZekay(`${this.zekayAPI}/excursion-bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 201);
    return normalizeExcursionBooking(await response.json());
  }

  async updateExcursionBooking(
    id: number,
    payload: UpdateExcursionBookingRequest,
  ): Promise<ExcursionBooking> {
    const response = await fetchZekay(`${this.zekayAPI}/excursion-bookings/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(response);
    return normalizeExcursionBooking(await response.json());
  }

  async deleteExcursionBooking(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/excursion-bookings/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async sendExcursionSMS(
    id: number,
    payload: SendExcursionSMSRequest,
  ): Promise<SendExcursionSMSResponse> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/${id}/sms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(response);
    return (await response.json()) as SendExcursionSMSResponse;
  }

  async listExcursionSMSMessages(id: number): Promise<ExcursionSMSMessagesResponse> {
    const response = await fetchZekay(`${this.zekayAPI}/excursions/${id}/sms`);
    await assertResponseOk(response);
    return (await response.json()) as ExcursionSMSMessagesResponse;
  }
}
